import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  PanelLeftClose,
  PanelLeft,
  ChevronUp,
  LogOut,
  Sun,
  Moon,
  Sparkles,
  Check,
  Building2,
  Compass,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAuth } from '@/store/AuthContext.tsx'
import { useTheme } from '@/store/ThemeContext.tsx'
import { useAiSidebar } from '@/store/AiSidebarContext'
import { useOrganization } from '@/store/OrganizationContext'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { navItems } from '@/components/layout/navItems'


interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  onOpenTour?: () => void
  /** スマホのドロワーで使うとき、ページを選んだらドロワーを閉じるため */
  onNavigate?: () => void
}

export function Sidebar({ collapsed, onToggle, onOpenTour, onNavigate }: SidebarProps) {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { isOpen: aiSidebarOpen, toggle: toggleAiSidebar, activeCampId } = useAiSidebar()
  const { memberships, currentMembership, switchOrganization } = useOrganization()
  const hasCamp = !!activeCampId
  const navigate = useNavigate()

  function handleLogout() {
    logout()
    navigate('/login', { replace: true })
  }

  const initials = user?.email?.charAt(0).toUpperCase() ?? '?'
  const tenantName = currentMembership?.name ?? 'CAMP'
  const tenantLogo = currentMembership?.logo_url ?? null
  const tenantInitial = (currentMembership?.name ?? 'C').charAt(0).toUpperCase()

  return (
    <TooltipProvider>
      <aside
        className={cn(
          'flex flex-col h-full bg-surface border-r border-border transition-[width] duration-200',
          collapsed ? 'w-16' : 'w-[220px]',
        )}
      >
        {/* Tenant brand — top-left. Multi-tenant: button hint + ring when 2+ orgs. */}
        {memberships.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              className={cn(
                'group flex items-center h-14 px-4 shrink-0 outline-none transition-colors hover:bg-surface-2 focus-visible:bg-surface-2',
                collapsed && 'justify-center px-0',
              )}
              aria-label={t('sidebar.switchTenant')}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="ring-1 ring-accent/40 rounded">
                  <TenantLogoBadge logoUrl={tenantLogo} fallback={tenantInitial} />
                </div>
                {!collapsed && (
                  <>
                    <span
                      className="font-[Outfit,system-ui,sans-serif] text-base font-black leading-none tracking-[0.02em] text-text truncate"
                      title={tenantName}
                    >
                      {tenantName}
                    </span>
                    <ChevronUp className="size-3 text-text-3 rotate-180 shrink-0" />
                  </>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side={collapsed ? 'right' : 'bottom'}
              align="start"
              className="min-w-[220px]"
            >
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-text-3">
                {t('sidebar.switchTenant')}
              </DropdownMenuLabel>
              {memberships.map((m) => {
                const active = m.organization_id === currentMembership?.organization_id
                return (
                  <DropdownMenuItem
                    key={m.organization_id}
                    onSelect={() => {
                      if (!active) switchOrganization(m.organization_id)
                    }}
                    className={cn('flex items-center justify-between gap-2', active && 'font-medium')}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <TenantLogoBadge logoUrl={m.logo_url} fallback={m.name.charAt(0).toUpperCase()} small />
                      <span className="truncate">{m.name}</span>
                    </span>
                    {active && <Check className="size-3.5 shrink-0 text-accent" />}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className={cn('flex items-center h-14 px-4 shrink-0', collapsed && 'justify-center px-0')}>
            <div className="flex items-center gap-2.5 min-w-0">
              <TenantLogoBadge logoUrl={tenantLogo} fallback={tenantInitial} />
              {!collapsed && (
                <span
                  className="font-[Outfit,system-ui,sans-serif] text-base font-black leading-none tracking-[0.02em] text-text truncate"
                  title={tenantName}
                >
                  {tenantName}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-2 py-2 space-y-0.5">
          {navItems.map((item) => {
            const link = (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors outline-none',
                    'text-text-2 hover:text-text hover:bg-surface-2',
                    'focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:ring-offset-0',
                    isActive && 'text-text bg-surface-2',
                    collapsed && 'justify-center px-0',
                  )
                }
              >
                <item.icon className="size-4 shrink-0" />
                {!collapsed && <span>{t(item.labelKey)}</span>}
              </NavLink>
            )

            if (collapsed) {
              return (
                <Tooltip key={item.to}>
                  <TooltipTrigger asChild>{link}</TooltipTrigger>
                  <TooltipContent side="right">{t(item.labelKey)}</TooltipContent>
                </Tooltip>
              )
            }

            return link
          })}
        </nav>

        {/* AI Assistant toggle */}
        <div className="px-2 py-1">
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={toggleAiSidebar}
                  disabled={!hasCamp}
                  className={cn(
                    'mx-auto text-text-2 hover:text-ai',
                    !hasCamp && 'opacity-40 cursor-not-allowed',
                    aiSidebarOpen && hasCamp && 'text-ai bg-ai/10',
                  )}
                  aria-label={hasCamp ? t('sidebar.openAi') : t('sidebar.aiNoCamp')}
                >
                  <Sparkles className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{hasCamp ? t('sidebar.aiAssistant') : t('sidebar.selectCampPrompt')}</TooltipContent>
            </Tooltip>
          ) : (
            (() => {
              const aiButton = (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleAiSidebar}
                  disabled={!hasCamp}
                  className={cn(
                    'w-full justify-start text-text-2 hover:text-ai',
                    !hasCamp && 'opacity-40 cursor-not-allowed',
                    aiSidebarOpen && hasCamp && 'text-ai bg-ai/10',
                  )}
                  aria-label={hasCamp ? t('sidebar.openAi') : t('sidebar.aiNoCamp')}
                >
                  <Sparkles className="size-4" />
                  <span className="flex-1 text-left text-xs">{t('sidebar.aiAssistant')}</span>
                </Button>
              )
              // When disabled, explain *why* — a greyed-out button with no
              // reason is confusing for non-technical users.
              if (!hasCamp) {
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block">{aiButton}</span>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {t('sidebar.selectCampToUseAi')}
                    </TooltipContent>
                  </Tooltip>
                )
              }
              return aiButton
            })()
          )}
        </div>

        {/* Collapse toggle */}
        <div className={cn('px-2 py-1', collapsed && 'flex justify-center')}>
          <Button
            variant="ghost"
            size={collapsed ? 'icon-sm' : 'sm'}
            onClick={onToggle}
            className={cn('text-text-2 hover:text-text', !collapsed && 'w-full justify-start')}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapseAria')}
          >
            {collapsed ? <PanelLeft className="size-4" /> : (
              <>
                <PanelLeftClose className="size-4" />
                <span className="flex-1 text-left text-xs">{t('sidebar.collapse')}</span>
              </>
            )}
          </Button>
        </div>

        {/* Profile menu — bottom-left (org switch + theme + logout) */}
        <DropdownMenu>
          <DropdownMenuTrigger
            className={cn(
              'flex items-center gap-3 border-t border-border px-4 py-3 shrink-0 outline-none',
              'transition-colors hover:bg-surface-2 focus-visible:bg-surface-2',
              collapsed && 'justify-center px-0',
            )}
            aria-label={t('sidebar.profileMenu')}
          >
            <Avatar size="sm">
              <AvatarFallback className="bg-text text-bg text-xs">{initials}</AvatarFallback>
            </Avatar>
            {!collapsed && (
              <div className="min-w-0 flex-1 text-left">
                <p className="text-xs font-medium text-text truncate">{user?.email ?? ''}</p>
              </div>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={collapsed ? 'right' : 'top'}
            align={collapsed ? 'end' : 'start'}
            className="min-w-[220px]"
          >
            {memberships.length > 0 && (
              <>
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-text-3">
                  {t('sidebar.switchTenant')}
                </DropdownMenuLabel>
                {memberships.map((m) => {
                  const active = m.organization_id === currentMembership?.organization_id
                  return (
                    <DropdownMenuItem
                      key={m.organization_id}
                      onSelect={() => {
                        if (!active) switchOrganization(m.organization_id)
                      }}
                      className={cn('flex items-center justify-between gap-2', active && 'font-medium')}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <TenantLogoBadge logoUrl={m.logo_url} fallback={m.name.charAt(0).toUpperCase()} small />
                        <span className="truncate">{m.name}</span>
                      </span>
                      {active && <Check className="size-3.5 shrink-0 text-accent" />}
                    </DropdownMenuItem>
                  )
                })}
                <DropdownMenuSeparator />
              </>
            )}
            {onOpenTour && (
              <DropdownMenuItem onSelect={onOpenTour} className="gap-2">
                <Compass className="size-4" />
                <span>{t('sidebar.quickTour')}</span>
              </DropdownMenuItem>
            )}
            <div className="flex items-center justify-between gap-2 px-2 py-1.5 text-sm">
              <span className="text-text-2">{t('common.language')}</span>
              <LanguageSwitcher />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={toggleTheme} className="gap-2">
              {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
              <span>{theme === 'dark' ? t('sidebar.lightMode') : t('sidebar.darkMode')}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleLogout} className="gap-2 text-danger">
              <LogOut className="size-4" />
              <span>{t('sidebar.logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </aside>
    </TooltipProvider>
  )
}

function TenantLogoBadge({
  logoUrl,
  fallback,
  small = false,
}: {
  logoUrl: string | null
  fallback: string
  small?: boolean
}) {
  const size = small ? 'size-5' : 'size-7'
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        className={cn(size, 'shrink-0 rounded object-cover bg-surface-2')}
      />
    )
  }
  return (
    <div
      className={cn(
        size,
        'shrink-0 rounded bg-text text-bg flex items-center justify-center',
        small ? 'text-[10px]' : 'text-[12px]',
        'font-bold tracking-tight',
      )}
    >
      {fallback || <Building2 className="size-3.5" />}
    </div>
  )
}
